import Blockly from 'blockly/browser'
import { defineBlock, getOptionsCode } from '../Util'
import Mutators from '../Mutators'

Mutators.registerOptionsMutator('sirenia_dialog_buttons_mutator', {
  buttons: {
    type: 'Array',
    label: 'buttons'
  },
  timeout: {
    type: 'Number',
    label: 'timeout (ms)'
  },
  sound: {
    type: 'String',
    label: 'sound'
  }
})

defineBlock(
  'sirenia_api_dialog_info',
  {
    message0: 'show %1 %2 header %3 text %4',
    args0: [
      {
        type: 'field_dropdown',
        name: 'SEVERITY',
        options: [['info', 'INFO'], ['warning', 'WARN']]
      },
      {
        type: 'input_dummy'
      },
      {
        type: 'input_value',
        name: 'HEADER',
        align: 'RIGHT'
      },
      {
        type: 'input_value',
        name: 'TEXT',
        align: 'RIGHT'
      }
    ],
    output: null,
    tooltip: 'Pauses flow and shows an info dialog',
    helpUrl: 'http://sirenia.eu/product/configuration-interface/documentation/flows/#info-dialog',
    mutator: 'sirenia_dialog_buttons_mutator'
  },
  function(block) {
    const severity = block.getFieldValue('SEVERITY')
    const header =
      Blockly.JavaScript.valueToCode(block, 'HEADER', Blockly.JavaScript.ORDER_ATOMIC) || "''"
    const text =
      Blockly.JavaScript.valueToCode(block, 'TEXT', Blockly.JavaScript.ORDER_ATOMIC) || "''"
    const optionsObject = getOptionsCode(this.chosenProps, block)
    const code = `Dialog.${severity.toLowerCase()}(${header}, ${text}, ${optionsObject})`
    return [code, Blockly.JavaScript.ORDER_ATOMIC]
  }
)

defineBlock(
  'sirenia_api_dialog_button',
  {
    message0: 'button %1 default: %2 cancels: %3',
    args0: [
      {
        type: 'field_input',
        name: 'VALUE',
        text: 'label'
      },
      {
        type: 'field_checkbox',
        name: 'IS_DEFAULT',
        checked: false
      },
      {
        type: 'field_checkbox',
        name: 'IS_CANCEL',
        checked: false
      }
    ],
    inputsInline: true,
    output: 'Button',
    tooltip: 'Dialog button',
    helpUrl: 'http://sirenia.eu/product/configuration-interface/documentation/flows/#info-dialog'
  },
  function(block) {
    const value = block.getFieldValue('VALUE') || 'label'
    const isDefault = block.getFieldValue('IS_DEFAULT') === 'TRUE'
    const isCancel = block.getFieldValue('IS_CANCEL') === 'TRUE'
    const code = `{ value: '${value}', isDefault: ${isDefault}, isCancel: ${isCancel} }`
    return [code, Blockly.JavaScript.ORDER_ATOMIC]
  }
)

Mutators.registerOptionsMutator('sirenia_dialog_input_mutator', {
  buttons: {
    type: 'Array',
    label: 'buttons'
  },
  timeout: {
    type: 'Number',
    label: 'timeout (ms)'
  },
  sound: {
    type: 'String',
    label: 'sound'
  },
  maxDialogWidth: {
    type: 'Number',
    label: 'max width'
  },
  maxDialogHeight: {
    type: 'Number',
    label: 'max height'
  }
})

defineBlock(
  'sirenia_api_dialog_input',
  {
    message0: 'user input %1 header %2 text %3 inputs %4',
    args0: [
      {
        type: 'input_dummy'
      },
      {
        type: 'input_value',
        name: 'HEADER',
        check: 'String',
        align: 'RIGHT'
      },
      {
        type: 'input_value',
        name: 'TEXT',
        check: 'String',
        align: 'RIGHT'
      },
      {
        type: 'input_value',
        name: 'INPUTS',
        align: 'RIGHT'
      }
    ],
    output: null,
    tooltip: 'Pauses flow and shows an info dialog',
    helpUrl: 'http://sirenia.eu/product/configuration-interface/documentation/flows/#info-dialog',
    mutator: 'sirenia_dialog_input_mutator'
  },
  function(block) {
    let inputProperties = {}
    block.inputList
      .filter(input => input.name === 'INPUTS')
      .forEach(input => {
        if (input.connection && input.connection.isConnected()) {
          const inputsObjectBlock = input.connection.targetBlock()
          inputsObjectBlock.inputList
            .filter(input => input.name && input.name !== '_TEMP_COLLAPSED_INPUT')
            .forEach(dialogInput => {
              inputProperties[dialogInput.name] =
                Blockly.JavaScript.valueToCode(
                  inputsObjectBlock,
                  dialogInput.name,
                  Blockly.JavaScript.ORDER_ATOMIC
                ) || ''
            })
        }
      })
    const header =
      Blockly.JavaScript.valueToCode(block, 'HEADER', Blockly.JavaScript.ORDER_ATOMIC) || "''"
    const text =
      Blockly.JavaScript.valueToCode(block, 'TEXT', Blockly.JavaScript.ORDER_ATOMIC) || "''"
    const optionsObject = getOptionsCode(this.chosenProps, block, inputProperties) // Include the input properties in the third argument options object
    const code = `Dialog.input(${header}, ${text}, ${optionsObject})`
    return [code, Blockly.JavaScript.ORDER_ATOMIC]
  }
)

defineBlock(
  'sirenia_api_dialog_html_input',
  {
    message0:
      'user input (html) %1 max width %2 max height %3 embed %4 %5 header %6 text %7 html source or url %8',
    args0: [
      {
        type: 'input_dummy'
      },
      {
        type: 'field_number',
        name: 'MAX_WIDTH',
        value: 0,
        min: 0,
        precision: 1
      },
      {
        type: 'field_number',
        name: 'MAX_HEIGHT',
        value: 0,
        min: 0,
        precision: 1
      },
      {
        type: 'field_checkbox',
        name: 'EMBED',
        checked: true
      },
      {
        type: 'input_dummy'
      },
      {
        type: 'input_value',
        name: 'HEADER',
        check: 'String',
        align: 'RIGHT'
      },
      {
        type: 'input_value',
        name: 'TEXT',
        check: 'String',
        align: 'RIGHT'
      },
      {
        type: 'input_value',
        name: 'SOURCE',
        check: 'String',
        align: 'RIGHT'
      }
    ],
    output: null,
    tooltip: 'Pauses flow and asks user for input via html form',
    helpUrl:
      'http://sirenia.eu/product/configuration-interface/documentation/flows/#html-based-input-dialog'
  },
  function(block) {
    const maxWidth = block.getFieldValue('MAX_WIDTH') || '0'
    const maxHeight = block.getFieldValue('MAX_HEIGHT') || '0'
    const embed = block.getFieldValue('EMBED') === 'TRUE'
    const header =
      Blockly.JavaScript.valueToCode(block, 'HEADER', Blockly.JavaScript.ORDER_ATOMIC) || "''"
    const text =
      Blockly.JavaScript.valueToCode(block, 'TEXT', Blockly.JavaScript.ORDER_ATOMIC) || "''"
    const source =
      Blockly.JavaScript.valueToCode(block, 'SOURCE', Blockly.JavaScript.ORDER_ATOMIC) || "''"
    const props = [
      `source: ${source}`,
      `embed: ${embed}`,
      `maxDialogWidth: ${maxWidth}`,
      `maxDialogHeight: ${maxHeight}`
    ]
    const code = `Dialog.inputHtml(${header}, ${text}, {\n${props
      .map(p => Blockly.JavaScript.INDENT + p)
      .join(',\n')}\n})`
    return [code, Blockly.JavaScript.ORDER_ATOMIC]
  }
)
